import {composeDecorators} from '@draft-js-plugins/editor';
import createFocusPlugin from '@draft-js-plugins/focus';
import '@draft-js-plugins/image/lib/plugin.css';
import createResizeablePlugin from '@draft-js-plugins/resizeable';
import createAlignmentPlugin from '../utils/AlignmentPlugin.cjs';
import '../utils/AlignmentPlugin.css';
import '../utils/FocusPlugin.css';
import createImagePlugin from '../utils/ImagePlugin.cjs';

import {ContentBlock, ContentState, EditorState} from 'draft-js';
import {CreateSheet, GetColor, css} from 'packages/aphrodite/aphrodite';
import ImageUpload from 'packages/files/ImageUpload.react';
import {EmptyFile, File} from 'packages/files/models';
import {Body, ComponentProps, Footer, Header, Lightbox, showLightbox} from 'packages/lightbox/lightbox';
import React, {ImgHTMLAttributes, PropsWithChildren, ReactElement, useContext, useRef, useState} from 'react';
import {MakeButton, PrimaryManagerFilled} from 'src/themes/button';
import {DraftEditorContext, DraftEditorFunctions} from '../DraftEditor.react';
import {MakeInput, PrimaryInput} from 'src/themes/input';
import useSafeState from 'packages/hooks/useSafeState';

import {ReactComponent as AlignBlockLeftIcon} from '../icons/align-block-left.svg';
import {ReactComponent as AlignBlockCenterIcon} from '../icons/align-block-center.svg';
import {ReactComponent as PencilIcon} from 'src/icons/pencil.svg';
import {LightTheme} from 'src/themes/lightbox';

export type Alignment = 'default' | 'center' | 'right';
export type Spacing = {
    top: number;
    right: number;
    bottom: number;
    left: number;
};
export const EmptySpacing = {
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
};

export type ImageData = {
    file: File;
    spacing?: Spacing;
    borderRadius?: number;
    alignment?: Alignment;
    width?: number;
    height?: number;
};
export const EmptyImageData = {
    file: EmptyFile,
    spacing: EmptySpacing,
    width: 40,
};

interface ImageComponentProps extends ImgHTMLAttributes<HTMLImageElement> {
    block: ContentBlock;
    contentState: ContentState;

    //removed props
    blockStyleFn: unknown;
    blockProps: unknown;
    customStyleMap: unknown;
    customStyleFn: unknown;
    decorator: unknown;
    forceSelection: unknown;
    offsetKey: unknown;
    selection: unknown;
    tree: unknown;
    preventScroll: unknown;
}

export const ImageComponent = React.forwardRef<HTMLImageElement, ImageComponentProps>((props, ref): ReactElement => {
    const [isHovered, setIsHovered] = useState(false);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const {functions, readOnly} = useContext(DraftEditorContext);

    const {block, contentState, ...otherProps} = props;
    const {
        blockProps, // eslint-disable-line @typescript-eslint/no-unused-vars
        customStyleMap, // eslint-disable-line @typescript-eslint/no-unused-vars
        customStyleFn, // eslint-disable-line @typescript-eslint/no-unused-vars
        decorator, // eslint-disable-line @typescript-eslint/no-unused-vars
        forceSelection, // eslint-disable-line @typescript-eslint/no-unused-vars
        offsetKey, // eslint-disable-line @typescript-eslint/no-unused-vars
        selection, // eslint-disable-line @typescript-eslint/no-unused-vars
        tree, // eslint-disable-line @typescript-eslint/no-unused-vars
        blockStyleFn, // eslint-disable-line @typescript-eslint/no-unused-vars
        preventScroll, // eslint-disable-line @typescript-eslint/no-unused-vars
        ...elementProps
    } = otherProps;

    const onMouseEnter = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        setIsHovered(true);
    };

    const onAlignmentsMouseEnter = () => {
        if (!isHovered) {
            return;
        }

        onMouseEnter();
    };

    const onMouseLeave = () => {
        timeoutRef.current = setTimeout(() => setIsHovered(false), 400);
    };

    const onEditClick = () => {
        const content = functions.getState().getCurrentContent();
        const selection = functions.getState().getSelection();
        const entityKey = content.getBlockForKey(selection.getAnchorKey()).getEntityAt(0);
        if (entityKey) {
            showLightbox({
                selector: 'InsertImage',
                Component: InsertImage,
                theme: LightTheme,
                content: {
                    imageData: content.getEntity(entityKey).getData() as ImageData,
                    onInsert: data => {
                        functions.onChange(
                            EditorState.set(functions.getState(), {
                                currentContent: content.mergeEntityData(entityKey, data),
                            })
                        );
                    },
                },
            });
        }
    };

    const onAlignmentClick = (alignment: Alignment) => () => {
        if (isAlignmentAllowed) {
            setAlignment(functions, entityKey, alignment);
        }
    };

    const entityKey = block.getEntityAt(0);
    const data = contentState.getEntity(entityKey).getData() as ImageData;

    const ImageWidth = document.getElementById(entityKey)?.getBoundingClientRect().width;
    const alignmentsContainerStyles: React.CSSProperties = {};

    switch (data.alignment || 'default') {
        case 'default':
            alignmentsContainerStyles.left = (ImageWidth || 0) / 2 - 80;
            break;
        case 'center':
            alignmentsContainerStyles.left = 'calc(50% - 80px)';
            break;
        case 'right':
            alignmentsContainerStyles.right = (ImageWidth || 0) / 2 - 80;
            break;
    }

    const isAlignmentAllowed = data.width ? data.width < 100 : true;

    return (
        <ImageRenderer
            {...{
                ref,
                data,
                readOnly,
                entityKey,
                onMouseEnter,
                onMouseLeave,
                elementProps,
            }}
        >
            {!readOnly && (
                <div
                    {...{
                        onMouseEnter: onAlignmentsMouseEnter,
                        onMouseLeave,
                        className: css(Styles.alignmentsContainer, isHovered && Styles.visible),
                        style: alignmentsContainerStyles,
                    }}
                >
                    <div className={css(Styles.alignmentItems)}>
                        <div
                            {...{
                                className: css(
                                    Styles.alignmentItem,
                                    !isAlignmentAllowed && Styles.alignmentItemDisabled
                                ),
                                onClick: onAlignmentClick('default'),
                            }}
                        >
                            <AlignBlockLeftIcon className={css(Styles.alignmentItemIcon)} />
                        </div>
                        <div className={css(Styles.divider)}></div>
                        <div
                            {...{
                                className: css(
                                    Styles.alignmentItem,
                                    !isAlignmentAllowed && Styles.alignmentItemDisabled
                                ),
                                onClick: onAlignmentClick('center'),
                            }}
                        >
                            <AlignBlockCenterIcon className={css(Styles.alignmentItemIcon)} />
                        </div>
                        <div className={css(Styles.divider)}></div>
                        <div
                            {...{
                                className: css(
                                    Styles.alignmentItem,
                                    !isAlignmentAllowed && Styles.alignmentItemDisabled
                                ),
                                onClick: onAlignmentClick('right'),
                            }}
                        >
                            <AlignBlockLeftIcon className={css(Styles.alignmentItemIcon, Styles.reversedIcon)} />
                        </div>
                    </div>
                    <div className={css(Styles.alignmentItems)}>
                        <div
                            {...{
                                className: css(Styles.alignmentItem),
                                onClick: onEditClick,
                            }}
                        >
                            <PencilIcon className={css(Styles.pencilIcon)} />
                        </div>
                    </div>
                </div>
            )}
        </ImageRenderer>
    );
});

export type ImageRendererProps = {
    data: ImageData;
    readOnly?: boolean;
    entityKey?: string;
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
    elementProps?: any;
};

export const ImageRenderer = React.forwardRef<HTMLImageElement, PropsWithChildren<ImageRendererProps>>(
    ({data, entityKey, readOnly, onMouseEnter, onMouseLeave, elementProps, children}, ref): ReactElement => {
        const additionalStyles: React.CSSProperties = {};

        switch (data.alignment) {
            case 'center':
                additionalStyles.marginLeft = 'auto';
                additionalStyles.marginRight = 'auto';
                break;
            case 'right':
                additionalStyles.marginLeft = 'auto';
                break;
        }

        return (
            <div
                {...{
                    style: {
                        position: 'relative',
                        marginLeft: data.spacing?.left,
                        marginRight: data.spacing?.right,
                        marginTop: data.spacing?.top,
                        marginBottom: data.spacing?.bottom,
                    },
                }}
            >
                <img
                    {...{
                        ref,
                        ...elementProps,
                        id: entityKey,
                        src: Object.values(data.file['sources'])[0].url,
                        alt: data.file.title,
                        className: elementProps?.className,
                        onMouseEnter,
                        onMouseLeave,
                        style: {
                            ...elementProps?.style,
                            ...additionalStyles,
                            display: 'block',
                            verticalAlign: 'top',
                            width: elementProps?.style?.width || (data.width && `${data.width}%`) || '100%',
                            borderRadius: data.borderRadius,
                            position: 'relative',
                            boxShadow: readOnly ? 'none' : undefined,
                            float: undefined,
                            maxWidth: '100%',
                        },
                    }}
                />
                {children}
            </div>
        );
    }
);

const FocusPlugin = createFocusPlugin();
export const AlignmentPlugin = createAlignmentPlugin();
const ResizeablePlugin = createResizeablePlugin();
export const ImagePlugin = createImagePlugin({
    decorator: composeDecorators(ResizeablePlugin.decorator, AlignmentPlugin.decorator, FocusPlugin.decorator),
    imageComponent: ImageComponent,
});

export const plugins = [FocusPlugin, AlignmentPlugin, ResizeablePlugin, ImagePlugin];

export const setAlignment = (draft: DraftEditorFunctions, entityKey: string, alignment: Alignment) => {
    const state = draft.getState();
    const content = state.getCurrentContent();

    const newContent = content.mergeEntityData(entityKey, {
        alignment: alignment,
    });

    const newEditorState = EditorState.set(state, {
        currentContent: newContent,
    });

    draft.onChange(newEditorState);
};

export interface InsertImageLightboxProps {
    imageData?: ImageData;
    onInsert: (data: ImageData) => void;
}

export default function InsertImage(data: ComponentProps<InsertImageLightboxProps>) {
    const [imageData, setImageData] = useSafeState<ImageData>(data.content.imageData ?? EmptyImageData);

    const onInsertClick = () => {
        if (imageData) {
            data.content.onInsert(imageData);
            data.close();
        }
    };

    const onSpacingInputChange = (field: keyof Spacing) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = parseInt(e.target.value || '0');

        if (!imageData.spacing) {
            setImageData({spacing: {$set: {...EmptySpacing, [field]: value}}});
            return;
        }

        setImageData({spacing: {[field]: {$set: value}}});
    };

    return (
        <Lightbox {...{close: data.close}}>
            <Header>{data.content.imageData ? 'Edit image' : 'Insert image'}</Header>
            <Body>
                <div className={css(Styles.uploadTitle)}>Please click gray rectangle to upload image.</div>
                <ImageUpload
                    {...{
                        size: 200,
                        file: imageData?.file,
                        OnUpload: file => setImageData({file: {$set: file}}),
                    }}
                />
                <div className={css(Styles.spacingTitle)}>Specify image spaces in pixels if needed.</div>
                <div className={css(Styles.inputs)}>
                    <div className={css(Styles.inputContainer)}>
                        Top space
                        <input
                            {...{
                                defaultValue: imageData.spacing?.top || 0,
                                className: css(MakeInput(PrimaryInput)),
                                onChange: onSpacingInputChange('top'),
                            }}
                        />
                    </div>
                    <div className={css(Styles.inputContainer)}>
                        Left space
                        <input
                            {...{
                                defaultValue: imageData.spacing?.left || 0,
                                className: css(MakeInput(PrimaryInput)),
                                onChange: onSpacingInputChange('left'),
                            }}
                        />
                    </div>
                </div>
                <div className={css(Styles.inputs)}>
                    <div className={css(Styles.inputContainer)}>
                        Bottom space
                        <input
                            {...{
                                defaultValue: imageData.spacing?.bottom || 0,
                                className: css(MakeInput(PrimaryInput)),
                                onChange: onSpacingInputChange('bottom'),
                            }}
                        />
                    </div>
                    <div className={css(Styles.inputContainer)}>
                        Right space
                        <input
                            {...{
                                defaultValue: imageData.spacing?.right || 0,
                                className: css(MakeInput(PrimaryInput)),
                                onChange: onSpacingInputChange('right'),
                            }}
                        />
                    </div>
                </div>
                <div className={css(Styles.spacingTitle)}>Specify border radius if needed.</div>
                <div className={css(Styles.inputs)}>
                    <div className={css(Styles.inputContainer)}>
                        Border radius
                        <input
                            {...{
                                defaultValue: imageData.borderRadius || 0,
                                className: css(MakeInput(PrimaryInput)),
                                onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                                    setImageData({borderRadius: {$set: parseInt(e.target.value || '0')}});
                                },
                            }}
                        />
                    </div>
                </div>
            </Body>
            <Footer
                {...{
                    styles: Styles.footer,
                }}
            >
                <button
                    {...{
                        disabled: imageData.file.id === 0,
                        className: css(MakeButton(PrimaryManagerFilled)),
                        onClick: onInsertClick,
                    }}
                >
                    {data.content.imageData ? 'Save' : 'Insert'}
                </button>
            </Footer>
        </Lightbox>
    );
}

const Styles = CreateSheet({
    uploadContainer: {
        display: 'flex',
        gap: 20,
    },
    imageUploadContainer: {
        flexShrink: 0,
    },
    uploadTitle: {
        marginBottom: 5,
    },
    spacingTitle: {
        marginTop: 10,
    },
    inputs: {
        display: 'flex',
        gap: 15,
        marginBottom: 10,
    },
    inputContainer: {
        flexGrow: 1,
        display: 'flex',
        gap: 5,
        flexDirection: 'column',
        fontSize: 12,
        color: GetColor('secondary'),
        fontWeight: 600,
    },
    footer: {
        display: 'flex',
        justifyContent: 'flex-end',
    },
    alignmentsContainer: {
        position: 'absolute',
        top: -45,
        opacity: 0,
        transition: 'opacity 0.25s ease-in-out',
        display: 'flex',
        gap: 10,
        pointerEvents: 'none',
    },
    visible: {
        opacity: 1,
        pointerEvents: 'auto',
    },
    alignmentItems: {
        display: 'flex',
        height: 36,
        border: `1px solid ${GetColor('gray2')}`,
        borderRadius: 5,
        overflow: 'hidden',
    },
    alignmentItem: {
        width: 36,
        background: GetColor('blockBg'),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        fill: GetColor('secondary'),
        ':hover': {
            fill: GetColor('primary'),
        },
    },
    alignmentItemDisabled: {
        cursor: 'not-allowed',
        ':hover': {
            fill: GetColor('secondary'),
        },
    },
    alignmentItemIcon: {
        width: 24,
        height: 24,
    },
    pencilIcon: {
        width: 20,
        height: 20,
    },
    reversedIcon: {
        transform: 'rotate(180deg)',
    },
    divider: {
        width: 1,
        height: '100%',
        background: GetColor('gray2'),
    },
});
